import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as ChildIcon,
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  Videocam as VideocamIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Налаштування axios для адміністраторів
const adminAxios = axios.create({
  baseURL: 'https://api.smartik.com.ua/api'
});

// Додаємо токен до всіх запитів
adminAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface DashboardStats {
  kindergartens: number;
  children: number;
  parents: number;
  menus: number;
  cameras: number;
  parentAccounts: number;
}

interface RecentActivity {
  id: string;
  type: string;
  message: string;
  timestamp: string;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Отримуємо статистику
      const [kindergartensRes, childrenRes, parentsRes, menusRes, camerasRes, parentAccountsRes] = await Promise.all([
        adminAxios.get('/kindergartens?limit=1'),
        adminAxios.get('/children?limit=1'),
        adminAxios.get('/parents?limit=1'),
        adminAxios.get('/menus?limit=1'),
        adminAxios.get('/cameras?limit=1'),
        adminAxios.get('/users?limit=1&role=parent'),
      ]);

      setStats({
        kindergartens: kindergartensRes.data.pagination?.totalItems || 0,
        children: childrenRes.data.pagination?.totalItems || 0,
        parents: parentsRes.data.pagination?.totalItems || 0,
        menus: menusRes.data.pagination?.totalItems || 0,
        cameras: camerasRes.data.pagination?.totalItems || 0,
        parentAccounts: parentAccountsRes.data.pagination?.totalItems || 0,
      });

      // Мокуємо останню активність
      setRecentActivities([
        {
          id: '1',
          type: 'child',
          message: 'Додано нову дитину: Іван Петренко',
          timestamp: new Date().toISOString(),
        },
        {
          id: '2',
          type: 'menu',
          message: 'Оновлено меню на 15 січня',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          id: '3',
          type: 'account',
          message: 'Створено обліковий запис для: maria@example.com',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
        },
        {
          id: '4',
          type: 'camera',
          message: 'Додано нову камеру: Група А',
          timestamp: new Date(Date.now() - 10800000).toISOString(),
        },
      ]);
    } catch (error) {
      console.error('Помилка завантаження даних:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'child':
        return <ChildIcon color="primary" />;
      case 'menu':
        return <RestaurantIcon color="secondary" />;
      case 'account':
        return <EmailIcon color="info" />;
      case 'camera':
        return <VideocamIcon color="success" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Щойно';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} годин тому`;
    } else {
      return date.toLocaleDateString('uk-UA');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Головна панель
      </Typography>

      {/* Статистика */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats?.kindergartens || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Дитячих садків
                </Typography>
              </Box>
            </Box>
            <Chip label="Активні" color="success" size="small" />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ChildIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats?.children || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Дітей
                </Typography>
              </Box>
            </Box>
            <Chip label="Зареєстровані" color="info" size="small" />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats?.parents || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Батьків
                </Typography>
              </Box>
            </Box>
            <Chip label="Активні" color="success" size="small" />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <RestaurantIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats?.menus || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Меню
                </Typography>
              </Box>
            </Box>
            <Chip label="Цього місяця" color="warning" size="small" />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <VideocamIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats?.cameras || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Камер
                </Typography>
              </Box>
            </Box>
            <Chip label="Онлайн" color="success" size="small" />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats?.parentAccounts || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Облікових записів
                </Typography>
              </Box>
            </Box>
            <Chip label="Активні" color="success" size="small" />
          </CardContent>
        </Card>
      </Box>

      {/* Остання активність */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Остання активність
            </Typography>
            <List>
              {recentActivities.map((activity) => (
                <ListItem key={activity.id} divider>
                  <ListItemIcon>
                    {getActivityIcon(activity.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.message}
                    secondary={formatTimestamp(activity.timestamp)}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard; 